import React from 'react';
/**
 * `Routing` defines the application's route table.
 *
 * It wires up the dealer, customer, and renewals journeys using nested route
 * managers (`DealerJourneyManager`, `CustomerJourneyManager`,
 * `RenewalsJourneyManager`) and maps shared pages such as payment assist return
 * handlers and under-maintenance/error pages.
 */
import { Routes, Route, useLocation, generatePath } from 'react-router-dom';
import UserNotFoundPage from './UserNotFoundPage';
import { PageAbsolutePath } from '../../consts/Common';
import AppUnderMaintenance from './AppUnderMaintenance';
import PaymentAssistFailure from './PaymentAssistFailure.';
import RenewalLandingPage from './renewals/landingPage/LandingPage';
import RenewalsFailedRegistration from './renewals/FailedRegistration';
import LateRenewalAcceptance from './renewals/LateRenewalAcceptance';
import RenewalsQuote from './renewals/quotes/RenewalsQuote';
import RenewalsConfirmation from './renewals/RenewalsConfirmation';
import RenewalsDirectDebit from './renewals/RenewalsDirectDebit';
import RenewalsJourneyManager from './renewals/RenewalsJourneyManager';
import RenewalsPersonalDetail from './renewals/RenewalsPersonalDetail';
import RenewalsSummary from './renewals/RenewalsSummary';
import RenewalsVehicleDetails from './renewals/RenewalsVehicleDetails';
import SinglePaymentPage from './SinglePaymentPage';
import CustomerConfirmationPage from './customer/CustomerConfirmationPage';
import CustomerCreatePlanPage from './customer/CustomerCreatePlanPage';
import CustomerDirectDebitPage from './customer/CustomerDirectDebitPage';
import CustomerJourneyManager from './customer/CustomerJourneyManager';
import CustomerLandingPage from './customer/CustomerLandingPage';
import CustomerPersonalDetail from './customer/CustomerPersonalDetail';
import CustomerProductPage from './customer/CustomerProductPage';
import CustomerSummaryPage from './customer/CustomerSummaryPage';
import CustomerVehicleDetailsPage from './customer/CustomerVehicleDetailsPage';
import FailedRegistration from './FailedRegistration';
import PaymentAssistSuccess from './PaymentAssistSuccess';
import Confirmation from './Confirmation';
import CreatePlanPage from './CreatePlanPage';
import DealerJourneyManager from './DealerJourneyManager';
import DirectDebitSetup from './DirectDebitSetup';
import LandingPage from './LandingPage';
import VehicleDetailsPage from './VehicleDetailsPage';
import PersonalDetailDealerPage from './PersonalDetailDealerPage';
import ProductPage from './ProductPage';
import SummaryPage from './SummaryPage';
import SuppressionPage from './suppression/SuppressionPage';
import WithdrawalPage from './WithdrawalPage';
import { AuthGuard } from '../../guards/AuthGuard';
import { useAppSelector } from '../../app-state/Hooks';

const Routing = (): JSX.Element => {
  const { search } = useLocation();
  const countryCode = useAppSelector((s) => s.appConfig.countryCode);

  const getParams = () => {
    const result = new URLSearchParams(search);

    return result;
  };

  return (
    <Routes>
      <Route
        path={PageAbsolutePath.Renewals.Landing}
        element={
          <RenewalLandingPage
            callCenterVrn={getParams()?.get('VRN') || ''}
            expAuthId={getParams()?.get('expauthid') || ''}
            campaignId={getParams()?.get('campaignid') || '0'}
          />
        }
      />
      <Route
        element={
          <AuthGuard
            redirectTo={generatePath(PageAbsolutePath.Renewals.Landing, {
              countrycode: countryCode
            })}
          />
        }>
        <Route
          path=':countrycode?/renewal/renewalsjourney'
          element={<RenewalsJourneyManager />}>
          <Route path='personaldetails' element={<RenewalsPersonalDetail />} />
          <Route path='vehicledetails' element={<RenewalsVehicleDetails />} />
          <Route path='quote' element={<RenewalsQuote />} />
          <Route path='summary' element={<RenewalsSummary />} />
          <Route path='directdebit' element={<RenewalsDirectDebit />} />
          <Route path='singlepayment' element={<SinglePaymentPage />} />

          <Route path='confirmation' element={<RenewalsConfirmation />} />
          <Route
            path='failedregistration'
            element={<RenewalsFailedRegistration />}
          />
          <Route
            path='lateRenewalAcceptance'
            element={<LateRenewalAcceptance />}
          />
        </Route>
      </Route>
      <Route
        path={PageAbsolutePath.Customer.Landing}
        element={
          <CustomerLandingPage
            id={getParams()?.get('id') ?? ''}
            dealerNumber={getParams()?.get('dealercode') ?? ''}
          />
        }
      />
      <Route
        path=':countrycode?/customer/customerjourney'
        element={<CustomerJourneyManager />}>
        <Route
          path='paymentassist/success'
          element={<PaymentAssistSuccess />}
        />
        <Route
          path='paymentassist/failure'
          element={<PaymentAssistFailure />}
        />
      </Route>
      <Route
        element={
          <AuthGuard
            redirectTo={generatePath(PageAbsolutePath.Customer.Landing, {
              countryCode
            })}
          />
        }>
        <Route
          path=':countrycode?/customer/customerjourney'
          element={<CustomerJourneyManager />}>
          <Route
            path='vehicledetails'
            element={<CustomerVehicleDetailsPage />}
          />
          <Route path='products' element={<CustomerProductPage />} />
          <Route path='createplan' element={<CustomerCreatePlanPage />} />
          <Route path='personaldetails' element={<CustomerPersonalDetail />} />
          <Route path='summary' element={<CustomerSummaryPage />} />
          <Route path='directdebit' element={<CustomerDirectDebitPage />} />
          <Route path='singlepayment' element={<SinglePaymentPage />} />
          <Route path='confirmation' element={<CustomerConfirmationPage />} />
          <Route path='failedregistration' element={<FailedRegistration />} />
        </Route>
      </Route>
      <Route
        path=':countrycode?/paymentassistfailure'
        element={<PaymentAssistFailure />}
      />
      <Route path=':countrycode?/errorpage' element={<UserNotFoundPage />} />
      <Route
        path=':countrycode?/undermaintenance'
        element={<AppUnderMaintenance />}
      />
      <Route
        path={PageAbsolutePath.Suppression}
        element={<SuppressionPage />}
      />
      <Route path={PageAbsolutePath.Withdrawal} element={<WithdrawalPage />} />
      <>
        <Route path={PageAbsolutePath.Landing} element={<LandingPage />} />
        <Route
          path=':countrycode?/dealerjourney'
          element={<DealerJourneyManager />}>
          <Route
            path='paymentassist/success'
            element={<PaymentAssistSuccess />}
          />
          <Route
            path='paymentassist/failure'
            element={<PaymentAssistFailure />}
          />
        </Route>
        <Route
          element={
            <AuthGuard
              redirectTo={generatePath(PageAbsolutePath.Landing, {
                countrycode: countryCode
              })}
            />
          }>
          <Route
            path=':countrycode?/dealerjourney'
            element={<DealerJourneyManager />}>
            <Route path='vehicledetails' element={<VehicleDetailsPage />} />
            <Route path='products' element={<ProductPage />} />
            <Route path='createplan' element={<CreatePlanPage />} />
            <Route
              path='personaldetails'
              element={<PersonalDetailDealerPage />}
            />
            <Route path='summary' element={<SummaryPage />} />
            <Route path='directdebit' element={<DirectDebitSetup />} />
            <Route path='singlepayment' element={<SinglePaymentPage />} />
            <Route path='confirmation' element={<Confirmation />} />
            <Route path='failedregistration' element={<FailedRegistration />} />
          </Route>
        </Route>
      </>
    </Routes>
  );
};

export default Routing;
